import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff, Loader2, ExternalLink } from 'lucide-react';
import { StoriesAPI, AdminAPI } from '../api';
import StoryForm from '../components/StoryForm';

const AdminHeader = ({ onLogout, activeTab, onTab }) => (
  <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
    <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-gray-500">Administration</div>
        <h1 className="text-xl md:text-2xl font-black text-gray-900" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          BCOOLSTORE Admin
        </h1>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onTab('list')}
          className={`px-4 py-2 text-[12px] uppercase font-bold tracking-wider transition-colors ${
            activeTab === 'list' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          Toutes les histoires
        </button>
        <button
          onClick={() => onTab('create')}
          className={`px-4 py-2 text-[12px] uppercase font-bold tracking-wider transition-colors ${
            activeTab === 'create' ? 'bg-[#8b2c3f] text-white' : 'bg-gray-900 text-white hover:bg-[#8b2c3f]'
          } inline-flex items-center gap-2`}
        >
          <Plus size={14} /> Nouvelle histoire
        </button>
        <Link to="/" className="px-4 py-2 text-[12px] uppercase font-bold tracking-wider text-gray-700 hover:bg-gray-100 inline-flex items-center gap-1">
          <ExternalLink size={14} /> Voir le site
        </Link>
        <button
          onClick={onLogout}
          className="px-4 py-2 text-[12px] uppercase font-bold tracking-wider text-gray-700 hover:bg-red-50 hover:text-red-700 inline-flex items-center gap-1"
        >
          <LogOut size={14} /> Déconnexion
        </button>
      </div>
    </div>
  </header>
);

const StatBadge = ({ label, value, color = 'bg-gray-900' }) => (
  <div className="bg-white border border-gray-200 px-5 py-3">
    <div className={`inline-block w-2 h-2 rounded-full ${color} mr-2 align-middle`} />
    <span className="text-[11px] uppercase tracking-widest text-gray-500">{label}</span>
    <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
  </div>
);

const Admin = () => {
  const navigate = useNavigate();
  const isLoggedIn = AdminAPI.isLoggedIn();
  const [tab, setTab] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // story object or null

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Charger même les non-publiés pour l'admin
      const data = await StoriesAPI.list({ limit: 200, order: 'desc', published: false });
      const dataAll = await StoriesAPI.list({ limit: 200, order: 'desc', published: true });
      // Combine et déduplique par id
      const seen = new Set();
      const merged = [...dataAll, ...data].filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
      setStories(merged);
    } catch (err) {
      setError(err.message || 'Erreur de chargement.');
      if (err.status === 401) {
        AdminAPI.logout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) load();
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoggedIn) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    AdminAPI.logout();
    navigate('/admin/login', { replace: true });
  };

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      await StoriesAPI.create(payload);
      await load();
      setTab('list');
    } catch (err) {
      alert(err.payload?.detail || err.message || 'Erreur à la création.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!editingId) return;
    setSubmitting(true);
    try {
      await StoriesAPI.update(editingId, payload);
      await load();
      setTab('list');
      setEditingId(null);
    } catch (err) {
      alert(err.payload?.detail || err.message || 'Erreur à la mise à jour.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await StoriesAPI.remove(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      alert(err.payload?.detail || err.message || 'Erreur à la suppression.');
    }
  };

  const editingStory = editingId ? stories.find((s) => s.id === editingId) : null;

  const publishedCount = stories.filter((s) => s.published).length;
  const draftCount = stories.length - publishedCount;

  return (
    <main className="min-h-[70vh] bg-gray-50">
      <AdminHeader onLogout={handleLogout} activeTab={tab} onTab={(t) => { setTab(t); if (t !== 'edit') setEditingId(null); }} />

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8">
        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBadge label="Histoires totales" value={stories.length} />
          <StatBadge label="Publiées" value={publishedCount} color="bg-green-600" />
          <StatBadge label="Brouillons" value={draftCount} color="bg-yellow-500" />
          <StatBadge label="Catégories" value={new Set(stories.map((s) => s.category_slug)).size} color="bg-[#8b2c3f]" />
        </div>

        {/* CONTENT */}
        {tab === 'list' && (
          <section className="bg-white border border-gray-200">
            <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-black text-gray-900">Toutes les histoires</h2>
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>

            {error && <div className="px-5 py-3 bg-red-50 text-red-700 border-b border-red-100 text-sm">{error}</div>}

            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-[10px] uppercase tracking-wider text-gray-600">
                  <th className="px-5 py-3">Titre</th>
                  <th className="px-5 py-3">Catégorie</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Statut</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stories.length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-gray-500 italic">
                      Aucune histoire pour l&apos;instant. Cliquez sur &quot;Nouvelle histoire&quot; pour commencer.
                    </td>
                  </tr>
                )}
                {stories.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <div className="flex items-start gap-3">
                        <img src={s.image} alt="" className="w-16 h-10 object-cover bg-gray-100 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-gray-900 line-clamp-2">{s.title}</div>
                          <div className="text-[11px] text-gray-500 font-mono mt-0.5">/{s.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[13px] text-gray-700">{s.category}</td>
                    <td className="px-5 py-3 text-[13px] text-gray-700 font-mono">{s.date}</td>
                    <td className="px-5 py-3">
                      {s.published ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-green-700 bg-green-50 px-2 py-1">
                          <Eye size={12} /> Publié
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-yellow-800 bg-yellow-50 px-2 py-1">
                          <EyeOff size={12} /> Brouillon
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/article/${s.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 text-gray-600 hover:text-[#8b2c3f] hover:bg-gray-100"
                          title="Voir sur le site"
                        >
                          <ExternalLink size={15} />
                        </Link>
                        <button
                          onClick={() => { setEditingId(s.id); setTab('edit'); }}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                          title="Modifier"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(s)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === 'create' && (
          <section className="bg-white border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Nouvelle histoire
            </h2>
            <StoryForm
              onCancel={() => setTab('list')}
              onSubmit={handleCreate}
              submitting={submitting}
            />
          </section>
        )}

        {tab === 'edit' && editingStory && (
          <section className="bg-white border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Modifier : <span className="text-[#8b2c3f]">{editingStory.title.slice(0, 60)}…</span>
            </h2>
            <StoryForm
              initial={editingStory}
              onCancel={() => { setTab('list'); setEditingId(null); }}
              onSubmit={handleUpdate}
              submitting={submitting}
            />
          </section>
        )}

        {tab === 'edit' && !editingStory && (
          <p className="text-gray-500 italic">Histoire introuvable.</p>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full p-6 md:p-8 shadow-xl">
            <h3 className="text-xl font-black text-gray-900 mb-2">Supprimer cette histoire ?</h3>
            <p className="text-sm text-gray-600 mb-1">Cette action est irréversible.</p>
            <p className="text-sm text-gray-900 font-semibold my-4 line-clamp-2">« {confirmDelete.title} »</p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-5 py-2 text-[12px] uppercase font-bold tracking-widest text-gray-700 hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 text-[12px] uppercase font-bold tracking-widest"
              >
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Admin;
